/**
 * COMPLIANCE MOCK API SERVICE
 * 
 * This service mimics the ComplianceClient that will be generated by NSwag.
 * It provides the same interface but uses mock data instead of real API calls.
 * 
 * When ready to switch to real API:
 * 1. Update USE_MOCK_DATA flag to false in complianceService.ts
 * 2. Ensure ComplianceClient exists in generated.ts
 */

import { mockActionItems, mockAuditors, mockAudits, mockAuditSchedules, mockAuditScoreTrends, mockAuditTemplates, mockAuditTypes, mockDashboardMetrics, mockJobRoles, mockNotifications, mockRegions, mockStores, mockTrainingAssignments, mockTrainingCategories, mockTrainingCertificates, mockTrainingCompletions, mockTrainingCompletionTrends, mockTrainingModules } from '../../../../data/complianceMockData';
import {
  AuditType,
  AuditTemplate,
  Audit,
  CreateAuditRequest,
  UpdateAuditRequest,
  SubmitAuditRequest,
  ApproveAuditRequest,
  ChallengeAuditRequest,
  AuditSchedule,
  TrainingModule,
  TrainingAssignment,
  TrainingCompletion,
  TrainingCertificate,
  TrainingCategory,
  CreateTrainingAssignmentRequest,
  ActionItem,
  JobRole,
  ComplianceNotification,
  DashboardMetrics,
  AuditScoreTrend,
  TrainingCompletionTrend,
  Store,
  Region,
  Auditor,
  ScheduleForm,
  UpdateAuditResponseRequest,
  AuditResponse,
  AuditReportFilter,
  AuditSummaryReport,
  AuditDetailReport,
  ActionItemsSummaryReport,
  ComplianceOverviewReport,
  ComplianceFranchise,
} from '../../../../pages/Compliance/Interfaces/complicanceInterfaces';

// Helper to simulate API delay
const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Clone helper for immutability
const clone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

// ========================================
// AUDITORS
// ========================================

export const getAllAuditors = async (_franchiseIds?: number[], _regionIds?: number[], _storeNumbers?: string[]): Promise<Auditor[]> => {
  await delay();
  return clone(mockAuditors);
};

// ========================================
// COMPLIANCE REFERENCE CONTROLLER (new endpoints)
// ========================================
export const getComplianceStores = async (includeInactive: boolean = false, franchiseIds?: number[], search?: string, take?: number): Promise<Store[]> => {
  await delay();
  let stores = includeInactive ? mockStores : mockStores.filter(s => s.isActive);
  if (franchiseIds && franchiseIds.length > 0) {
    stores = stores.filter(s => franchiseIds.includes(s.franchiseId));
  }
  if (search) {
    const term = search.toLowerCase();
    stores = stores.filter(s => s.storeName.toLowerCase().includes(term) || s.storeNumber.toLowerCase().includes(term) || s.region?.toLowerCase().includes(term));
  }
  if (take && take > 0) {
    stores = stores.slice(0, take);
  }
  return clone(stores);
};

export const getComplianceStoreById = async (id: number): Promise<Store> => {
  await delay();
  const store = mockStores.find(s => s.id === id);
  if (!store) throw new Error(`Store with ID ${id} not found`);
  return clone(store);
};

export const getComplianceStoreByNumber = async (storeNumber: string): Promise<Store> => {
  await delay();
  const store = mockStores.find(s => s.storeNumber === storeNumber);
  if (!store) throw new Error(`Store with number ${storeNumber} not found`);
  return clone(store);
};

export const getComplianceRegions = async (franchiseIds?: number[]): Promise<Region[]> => {
  await delay();
  return clone(mockRegions);
};

export const getTenantFranchises = async (storeNumber: string): Promise<ComplianceFranchise[]> => {
  await delay();
  return [];
};

// ========================================
// DASHBOARD
// ========================================
export const getDashboardMetrics = async (
  storeNumbers?: string[],
  regions?: string[],
): Promise<DashboardMetrics> => {
  await delay();
  
  // Filter data based on store numbers or regions
  let filteredAudits = mockAudits;
  let filteredActionItems = mockActionItems;
  
  // Apply store filter
  if (storeNumbers && storeNumbers.length > 0) {
    filteredAudits = filteredAudits.filter(a => storeNumbers.includes(a.storeNumber));
    filteredActionItems = filteredActionItems.filter(ai => storeNumbers.includes(ai.storeNumber));
  }
  
  // Apply region filter
  if (regions && regions.length > 0) {
    const storesInRegions = mockStores.filter(s => regions.includes(s.region)).map(s => s.storeNumber);
    filteredAudits = filteredAudits.filter(a => storesInRegions.includes(a.storeNumber));
    filteredActionItems = filteredActionItems.filter(ai => storesInRegions.includes(ai.storeNumber));
  }
  
  // Calculate metrics based on filtered data
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const auditsThisMonth = filteredAudits.filter(a => {
    if (!a.scheduledDate) return false;
    const auditDate = new Date(a.scheduledDate);
    return auditDate.getMonth() === currentMonth && auditDate.getFullYear() === currentYear;
  });
  
  const submittedAuditsThisMonth = auditsThisMonth.filter(a => 
    a.status === 3, // Submitted (AuditStatus.Submitted = 3)
  ).length;
  
  const submittedAudits = filteredAudits.filter(a => a.status === 3 && a.percentageScore !== null);
  const averageScore = submittedAudits.length > 0
    ? submittedAudits.reduce((sum, a) => sum + (a.percentageScore || 0), 0) / submittedAudits.length
    : 0;
  
  // Action Items
  const openActionItems = filteredActionItems.filter(ai => ai.status === 'Open' || ai.status === 'InProgress').length;
  const criticalActionItems = filteredActionItems.filter(ai => 
    (ai.status === 'Open' || ai.status === 'InProgress') && ai.priority === 'Critical',
  ).length;
  
  const currentWeekStart = new Date();
  currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
  const actionItemsCompletedThisWeek = filteredActionItems.filter(ai => {
    if (ai.status === 'Resolved' && ai.resolvedDate) {
      const resolvedDate = new Date(ai.resolvedDate);
      return resolvedDate >= currentWeekStart;
    }
    return false;
  }).length;
  
  // Calculate overall compliance score
  const actionItemScore = openActionItems > 0 
    ? Math.max(0, 100 - (openActionItems * 5) - (criticalActionItems * 10))
    : 100;
  
  const overallComplianceScore = (averageScore * 0.6) + (actionItemScore * 0.4);
  
  // Determine risk level based on score and critical issues
  let riskLevel: 'Low' | 'Medium' | 'High' | 'Critical' = 'Low';
  if (overallComplianceScore < 60 || criticalActionItems > 3) {
    riskLevel = 'Critical';
  } else if (overallComplianceScore < 75 || criticalActionItems > 1) {
    riskLevel = 'High';
  } else if (overallComplianceScore < 85 || criticalActionItems > 0) {
    riskLevel = 'Medium';
  }
  
  return {
    overallComplianceScore,
    riskLevel,
    criticalIssuesCount: criticalActionItems,
    auditCompletionRate: auditsThisMonth.length > 0
      ? (submittedAuditsThisMonth / auditsThisMonth.length) * 100
      : 0,
    completedAuditsThisMonth: submittedAuditsThisMonth,
    totalAuditsThisMonth: auditsThisMonth.length,
    averageAuditScore: averageScore,
    auditScoreTrend: mockDashboardMetrics.auditScoreTrend,
    openActionItems,
    criticalActionItems,
    actionItemsCompletedThisWeek,
    actionItemsTrend: mockDashboardMetrics.actionItemsTrend,
  };
};

export const getAuditScoreTrends = async (
  months: number = 6,
  _storeNumbers?: string[],
  _regions?: string[],
): Promise<AuditScoreTrend[]> => {
  await delay();
  
  // For trends, we'll use the mock data as-is for simplicity
  // In a real implementation, you'd filter and recalculate trends
  return clone(mockAuditScoreTrends.slice(-months));
};

export const getTrainingCompletionTrends = async (
  months: number = 6,
  _storeNumbers?: string[],
  _regions?: string[],
): Promise<TrainingCompletionTrend[]> => {
  await delay();
  
  // For trends, we'll use the mock data as-is for simplicity
  // In a real implementation, you'd filter and recalculate trends
  return clone(mockTrainingCompletionTrends.slice(-months));
};

// ========================================
// AUDIT TYPES
// ========================================
export const getAllAuditTypes = async (includeInactive: boolean = false, _storeNumber?: string): Promise<AuditType[]> => {
  await delay();
  const types = includeInactive ? mockAuditTypes : mockAuditTypes.filter(t => t.isActive);
  return clone(types);
};

export const getAuditTypeById = async (id: number, _storeNumber?: string): Promise<AuditType> => {
  await delay();
  const type = mockAuditTypes.find(t => t.id === id);
  if (!type) throw new Error(`Audit type with id ${id} not found`);
  return clone(type);
};

export const createAuditType = async (data: Omit<AuditType, 'id' | 'createdDate' | 'updatedDate'>, _storeNumber?: string): Promise<AuditType> => {
  await delay();
  const newType: AuditType = {
    ...data,
    id: Math.max(...mockAuditTypes.map(t => t.id)) + 1,
    createdDate: new Date().toISOString(),
    updatedDate: null,
  };
  mockAuditTypes.push(newType);
  return clone(newType);
};

export const updateAuditType = async (id: number, data: Partial<AuditType>, _storeNumber?: string): Promise<AuditType> => {
  await delay();
  const index = mockAuditTypes.findIndex(t => t.id === id);
  if (index === -1) throw new Error(`Audit type with id ${id} not found`);
  
  mockAuditTypes[index] = {
    ...mockAuditTypes[index],
    ...data,
    updatedDate: new Date().toISOString(),
  };
  return clone(mockAuditTypes[index]);
};

export const deleteAuditType = async (id: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockAuditTypes.findIndex(t => t.id === id);
  if (index === -1) throw new Error(`Audit type with id ${id} not found`);
  mockAuditTypes.splice(index, 1);
};

// ========================================
// AUDIT TEMPLATES
// ========================================
export const getAllAuditTemplates = async (auditTypeId?: number, includeUnpublished: boolean = false, _storeNumber?: string, franchiseId?: number): Promise<AuditTemplate[]> => {
  await delay();
  let templates = mockAuditTemplates;
  
  if (auditTypeId) {
    templates = templates.filter(t => t.auditTypeId === auditTypeId);
  }
  
  if (!includeUnpublished) {
    templates = templates.filter(t => t.isPublished);
  }

  if (franchiseId) {
    templates = templates.filter(t => t.franchises?.some(f => f.franchiseId === franchiseId));
  }
  
  return clone(templates);
};

export const getAuditTemplateById = async (id: number, _storeNumber?: string): Promise<AuditTemplate> => {
  await delay();
  const template = mockAuditTemplates.find(t => t.id === id);
  if (!template) throw new Error(`Audit template with id ${id} not found`);
  return clone(template);
};

export const createAuditTemplate = async (data: Omit<AuditTemplate, 'id' | 'createdDate' | 'publishedDate'>, _storeNumber?: string): Promise<AuditTemplate> => {
  await delay();
  const newTemplate: AuditTemplate = {
    ...data,
    id: Math.max(...mockAuditTemplates.map(t => t.id)) + 1,
    createdDate: new Date().toISOString(),
    publishedDate: data.isPublished ? new Date().toISOString() : null,
  };
  mockAuditTemplates.push(newTemplate);
  return clone(newTemplate);
};

export const updateAuditTemplate = async (id: number, data: Partial<AuditTemplate>, _storeNumber?: string): Promise<AuditTemplate> => {
  await delay();
  const index = mockAuditTemplates.findIndex(t => t.id === id);
  if (index === -1) throw new Error(`Audit template with id ${id} not found`);
  
  mockAuditTemplates[index] = {
    ...mockAuditTemplates[index],
    ...data,
  };
  return clone(mockAuditTemplates[index]);
};

export const deleteAuditTemplate = async (id: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockAuditTemplates.findIndex(t => t.id === id);
  if (index === -1) throw new Error(`Audit template with id ${id} not found`);
  mockAuditTemplates.splice(index, 1);
};

export const publishAuditTemplate = async (id: number, _storeNumber?: string): Promise<AuditTemplate> => {
  await delay();
  const index = mockAuditTemplates.findIndex(t => t.id === id);
  if (index === -1) throw new Error(`Audit template with id ${id} not found`);
  
  mockAuditTemplates[index].isPublished = true;
  mockAuditTemplates[index].publishedDate = new Date().toISOString();
  
  return clone(mockAuditTemplates[index]);
};

// ========================================
// AUDITS
// ========================================
export const getAllAudits = async (storeNumber?: string, status?: number, startDate?: string, endDate?: string): Promise<Audit[]> => {
  await delay();
  let audits = mockAudits;
  
  if (storeNumber) {
    audits = audits.filter(a => a.storeNumber === storeNumber);
  }
  
  if (status !== undefined) {
    audits = audits.filter(a => a.status === status);
  }
  
  if (startDate) {
    audits = audits.filter(a => a.scheduledDate && a.scheduledDate >= startDate);
  }
  
  if (endDate) {
    audits = audits.filter(a => a.scheduledDate && a.scheduledDate <= endDate);
  }
  
  return clone(audits);
};

export const getAuditById = async (id: number, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const audit = mockAudits.find(a => a.id === id);
  if (!audit) throw new Error(`Audit with id ${id} not found`);
  return clone(audit);
};

export const createAudit = async (data: CreateAuditRequest, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const template = mockAuditTemplates.find(t => t.id === data.templateId);
  if (!template) throw new Error(`Template with id ${data.templateId} not found`);
  
  const newAudit: Audit = {
    id: Math.max(...mockAudits.map(a => a.id)) + 1,
    auditTypeId: template.auditTypeId,
    auditTypeName: template.auditTypeName,
    templateId: data.templateId,
    templateName: template.name,
    storeNumber: data.storeNumber,
    storeName: `Store ${data.storeNumber}`,
    scheduledDate: data.scheduledDate,
    startedDate: null,
    completedDate: null,
    status: 0, // Draft
    assignedToUserId: data.assignedToUserId ?? 0,
    assignedToUserName: 'Assigned User',
    conductedByUserId: null,
    conductedByUserName: null,
    score: null,
    maxScore: template.totalPossibleScore,
    percentageScore: null,
    comments: null,
    responses: [],
    actionItemsCount: 0,
    firstSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureData: null,
    secondSignatureUserId: null,
    secondSignatureUserName: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  };
  
  mockAudits.push(newAudit);
  return clone(newAudit);
};

export const updateAudit = async (id: number, data: UpdateAuditRequest, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  
  mockAudits[index] = {
    ...mockAudits[index],
    scheduledDate: data.scheduledDate !== null ? data.scheduledDate : mockAudits[index].scheduledDate,
    assignedToUserName: mockAudits[index].assignedToUserName,
    assignedToUserId: data.assignedToUserId || mockAudits[index].assignedToUserId, // Legacy field
    comments: data.comments !== undefined ? data.comments : mockAudits[index].comments,
  };
  
  return clone(mockAudits[index]);
};

export const submitAudit = async (id: number, data: SubmitAuditRequest, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  
  // Calculate score from responses
  const totalScore = mockAudits[index].responses.reduce((sum, r) => sum + (r.score || 0), 0);
  const percentageScore = mockAudits[index].maxScore ? (totalScore / mockAudits[index].maxScore!) * 100 : 0;
  
  // Assume the current user is conducting the audit
  const currentUserId = 1; // Mock current user ID
  const currentUserName = 'Current User';
  
  mockAudits[index] = {
    ...mockAudits[index],
    completedDate: new Date().toISOString(),
    status: 3, // Submitted
    conductedByUserId: currentUserId,
    conductedByUserName: currentUserName,
    score: totalScore,
    percentageScore,
    comments: data.comments || mockAudits[index].comments,
    firstSignatureData: data.signatureData || null,
    firstSignatureUserId: currentUserId,
    firstSignatureUserName: currentUserName,
    firstSignatureDate: new Date().toISOString(),
  };
  
  return clone(mockAudits[index]);
};

export const approveAudit = async (id: number, data: ApproveAuditRequest, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  
  mockAudits[index] = {
    ...mockAudits[index],
    status: 3, // Stays Submitted - approve records sign-off but doesn't change status
    secondSignatureData: data.signatureData || null,
    secondSignatureUserId: 2,
    secondSignatureUserName: 'Reviewer User',
    secondSignatureDate: new Date().toISOString(),
  };
  
  return clone(mockAudits[index]);
};

export const challengeAudit = async (id: number, data: ChallengeAuditRequest, _storeNumber?: string): Promise<Audit> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  
  mockAudits[index] = {
    ...mockAudits[index],
    comments: data.comments,
  };
  
  return clone(mockAudits[index]);
};

export const deleteAudit = async (id: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  mockAudits.splice(index, 1);
};

export const updateAuditResponses = async (id: number, data: UpdateAuditResponseRequest, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockAudits.findIndex(a => a.id === id);
  if (index === -1) throw new Error(`Audit with id ${id} not found`);
  
  // Save responses to mock audit
  mockAudits[index] = {
    ...mockAudits[index],
    //status: mockAudits[index].status === 0 || mockAudits[index].status === 1 ? 2 : mockAudits[index].status, // Move to InProgress if Draft/Scheduled
    responses: data.responses as unknown as AuditResponse[],
    score: data.responses.reduce((sum, r) => sum + (r.score || 0), 0),
  };
};

export const uploadAuditPhoto = async (_auditId: number, _questionId: number, _file: File | Blob, _storeNumber?: string): Promise<string> => {
  await delay();
  // Mock: return a fake URL
  return `https://mock-storage.blob.core.windows.net/audit-photos/mock-${Date.now()}.jpg`;
};

export const downloadAuditPdf = async (auditId: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const audit = mockAudits.find(a => a.id === auditId);
  if (!audit) throw new Error(`Audit with id ${auditId} not found`);
  // Mock: generate a simple text blob as a placeholder PDF
  const blob = new Blob([`Mock PDF for Audit #${auditId}`], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Audit_${audit.storeNumber}_${audit.auditTypeName?.replace(/ /g, '_') ?? 'audit'}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const previewAuditPdf = async (auditId: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const audit = mockAudits.find(a => a.id === auditId);
  if (!audit) throw new Error(`Audit with id ${auditId} not found`);
  const blob = new Blob([`Mock PDF Preview for Audit #${auditId}`], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// ========================================
// AUDIT SCHEDULES
// ========================================
export const getAllAuditSchedules = async (templateId?: number, storeId?: number, includeInactive: boolean = false, _storeNumber?: string): Promise<AuditSchedule[]> => {
  await delay();
  let schedules = mockAuditSchedules;
  
  if (templateId) {
    schedules = schedules.filter(s => s.templateId === templateId);
  }
  
  if (storeId) {
    const store = mockStores.find(s => s.id === storeId);
    if (store) {
      schedules = schedules.filter(s => s.storeNumbers.includes(store.storeNumber));
    }
  }
  
  if (!includeInactive) {
    schedules = schedules.filter(s => s.isActive);
  }
  
  return clone(schedules);
};

export const getAuditScheduleById = async (id: number, _storeNumber?: string): Promise<AuditSchedule> => {
  await delay();
  const schedule = mockAuditSchedules.find(s => s.id === id);
  if (!schedule) throw new Error(`Audit schedule with id ${id} not found`);
  return clone(schedule);
};

export const createAuditSchedule = async (data: Omit<ScheduleForm, 'id' | 'createdDate' | 'updatedDate' | 'templateName' | 'storeName' | 'nextScheduledDate'>, _storeNumber?: string): Promise<AuditSchedule> => {
  await delay();
  const template = mockAuditTemplates.find(t => t.id === data.templateId);
  if (!template) throw new Error(`Template with id ${data.templateId} not found`);

  // Map storeNumbers to storeNumbers for the response
  const storeNumbers = data.storeNumbers || [];
  const newSchedule: AuditSchedule = {
    id: Math.max(...mockAuditSchedules.map(s => s.id)) + 1,
    templateId: data.templateId,
    templateName: template.name,
    franchiseId: template.franchises?.[0]?.franchiseId,
    assignmentType: data.assignmentType || 'all',
    storeNumbers,
    regionIds: data.regionIds || [],
    storeName: storeNumbers.length === 1
      ? (mockStores.find(s => s.storeNumber === storeNumbers[0])?.storeName || storeNumbers[0])
      : storeNumbers.length > 1
        ? `${storeNumbers.length} stores`
        : 'All Stores',
    cronExpression: data.cronExpression,
    isActive: data.isActive,
    isAdHoc: data.isAdHoc ?? false,
    assignToUserIds: data.assignToUserIds,
    notifyOnCreate: data.notifyOnCreate,
    nextScheduledDate: undefined,
    createdDate: new Date().toISOString(),
    updatedDate: undefined,
  };

  mockAuditSchedules.push(newSchedule);
  return clone(newSchedule);
};

export const updateAuditSchedule = async (id: number, data: Partial<ScheduleForm>, _storeNumber?: string): Promise<AuditSchedule> => {
  await delay();
  const index = mockAuditSchedules.findIndex(s => s.id === id);
  if (index === -1) throw new Error(`Audit schedule with id ${id} not found`);

  // Update template name if templateId changes
  let templateName = mockAuditSchedules[index].templateName;
  if (data.templateId && data.templateId !== mockAuditSchedules[index].templateId) {
    const template = mockAuditTemplates.find(t => t.id === data.templateId);
    if (template) {
      templateName = template.name;
    }
  }

  // Update store name if storeNumbers change
  let storeName = mockAuditSchedules[index].storeName;
  let storeNumbers = mockAuditSchedules[index].storeNumbers;
  if (Array.isArray(data.storeNumbers) && data.storeNumbers.length > 0) {
    storeNumbers = data.storeNumbers;
    const matchedStore = mockStores.find(s => s.storeNumber === storeNumbers[0]);
    storeName = storeNumbers.length === 1
      ? (matchedStore?.storeName || storeNumbers[0])
      : `${storeNumbers.length} stores`;
  }

  mockAuditSchedules[index] = {
    ...mockAuditSchedules[index],
    ...data,
    templateName,
    storeName,
    storeNumbers,
    updatedDate: new Date().toISOString(),
  };

  return clone(mockAuditSchedules[index]);
};

export const deleteAuditSchedule = async (id: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockAuditSchedules.findIndex(s => s.id === id);
  if (index === -1) throw new Error(`Audit schedule with id ${id} not found`);
  mockAuditSchedules.splice(index, 1);
};

export const triggerAuditSchedule = async (id: number, _storeNumber?: string): Promise<number> => {
  await delay();
  const schedule = mockAuditSchedules.find(s => s.id === id);
  if (!schedule) throw new Error(`Audit schedule with id ${id} not found`);
  // Mock: return the number of stores as "audits created"
  return schedule.storeNumbers?.length || 1;
};

// ========================================
// TRAINING CATEGORIES
// ========================================
export const getAllTrainingCategories = async (includeInactive: boolean = false): Promise<TrainingCategory[]> => {
  await delay();
  const categories = includeInactive ? mockTrainingCategories : mockTrainingCategories.filter(c => c.isActive);
  return clone(categories);
};

export const getTrainingCategoryById = async (id: number): Promise<TrainingCategory> => {
  await delay();
  const category = mockTrainingCategories.find(c => c.id === id);
  if (!category) throw new Error(`Training category with id ${id} not found`);
  return clone(category);
};

export const createTrainingCategory = async (data: Omit<TrainingCategory, 'id'>): Promise<TrainingCategory> => {
  await delay();
  const newCategory: TrainingCategory = {
    ...data,
    id: Math.max(...mockTrainingCategories.map(c => c.id)) + 1,
  };
  mockTrainingCategories.push(newCategory);
  return clone(newCategory);
};

export const updateTrainingCategory = async (id: number, data: Partial<TrainingCategory>): Promise<TrainingCategory> => {
  await delay();
  const index = mockTrainingCategories.findIndex(c => c.id === id);
  if (index === -1) throw new Error(`Training category with id ${id} not found`);
  
  mockTrainingCategories[index] = {
    ...mockTrainingCategories[index],
    ...data,
  };
  return clone(mockTrainingCategories[index]);
};

export const deleteTrainingCategory = async (id: number): Promise<void> => {
  await delay();
  const index = mockTrainingCategories.findIndex(c => c.id === id);
  if (index === -1) throw new Error(`Training category with id ${id} not found`);
  mockTrainingCategories.splice(index, 1);
};

// ========================================
// TRAINING MODULES
// ========================================
export const getAllTrainingModules = async (categoryId?: number, status?: number): Promise<TrainingModule[]> => {
  await delay();
  let modules = mockTrainingModules;
  
  if (categoryId) {
    modules = modules.filter(m => m.categoryId === categoryId);
  }
  
  if (status !== undefined) {
    modules = modules.filter(m => m.status === status);
  }
  
  return clone(modules);
};

export const getTrainingModuleById = async (id: number): Promise<TrainingModule> => {
  await delay();
  const module = mockTrainingModules.find(m => m.id === id);
  if (!module) throw new Error(`Training module with id ${id} not found`);
  return clone(module);
};

export const createTrainingModule = async (data: Omit<TrainingModule, 'id' | 'createdDate' | 'publishedDate'>): Promise<TrainingModule> => {
  await delay();
  const newModule: TrainingModule = {
    ...data,
    id: Math.max(...mockTrainingModules.map(m => m.id)) + 1,
    createdDate: new Date().toISOString(),
    publishedDate: data.status === 1 ? new Date().toISOString() : null, // 1 = Published
  };
  mockTrainingModules.push(newModule);
  return clone(newModule);
};

export const updateTrainingModule = async (id: number, data: Partial<TrainingModule>): Promise<TrainingModule> => {
  await delay();
  const index = mockTrainingModules.findIndex(m => m.id === id);
  if (index === -1) throw new Error(`Training module with id ${id} not found`);
  
  mockTrainingModules[index] = {
    ...mockTrainingModules[index],
    ...data,
  };
  return clone(mockTrainingModules[index]);
};

export const deleteTrainingModule = async (id: number): Promise<void> => {
  await delay();
  const index = mockTrainingModules.findIndex(m => m.id === id);
  if (index === -1) throw new Error(`Training module with id ${id} not found`);
  mockTrainingModules.splice(index, 1);
};

// ========================================
// TRAINING ASSIGNMENTS
// ========================================
export const getAllTrainingAssignments = async (userId?: number, moduleId?: number, status?: number): Promise<TrainingAssignment[]> => {
  await delay();
  let assignments = mockTrainingAssignments;
  
  if (userId) {
    assignments = assignments.filter(a => a.userId === userId);
  }
  
  if (moduleId) {
    assignments = assignments.filter(a => a.moduleId === moduleId);
  }
  
  if (status !== undefined) {
    assignments = assignments.filter(a => a.status === status);
  }
  
  return clone(assignments);
};

export const getTrainingAssignmentById = async (id: number): Promise<TrainingAssignment> => {
  await delay();
  const assignment = mockTrainingAssignments.find(a => a.id === id);
  if (!assignment) throw new Error(`Training assignment with id ${id} not found`);
  return clone(assignment);
};

export const createTrainingAssignment = async (data: CreateTrainingAssignmentRequest): Promise<TrainingAssignment[]> => {
  await delay();
  const module = mockTrainingModules.find(m => m.id === data.moduleId);
  if (!module) throw new Error(`Training module with id ${data.moduleId} not found`);
  
  const newAssignments: TrainingAssignment[] = data.userIds.map((userId, index) => ({
    id: Math.max(...mockTrainingAssignments.map(a => a.id)) + index + 1,
    moduleId: data.moduleId,
    moduleName: module.name,
    userId,
    userName: `User ${userId}`,
    userEmail: `user${userId}@example.com`,
    assignedDate: new Date().toISOString(),
    dueDate: data.dueDate,
    completedDate: null,
    status: 3, // Assigned
    score: null,
    attemptCount: 0,
  }));
  
  mockTrainingAssignments.push(...newAssignments);
  return clone(newAssignments);
};

// ========================================
// TRAINING COMPLETIONS & CERTIFICATES
// ========================================
export const getTrainingCompletionsByUserId = async (userId: number): Promise<TrainingCompletion[]> => {
  await delay();
  const completions = mockTrainingCompletions.filter(c => c.userId === userId);
  return clone(completions);
};

export const getTrainingCertificatesByUserId = async (userId: number): Promise<TrainingCertificate[]> => {
  await delay();
  const certificates = mockTrainingCertificates.filter(c => c.userId === userId);
  return clone(certificates);
};

export const getTrainingCertificateById = async (id: number): Promise<TrainingCertificate> => {
  await delay();
  const certificate = mockTrainingCertificates.find(c => c.id === id);
  if (!certificate) throw new Error(`Training certificate with id ${id} not found`);
  return clone(certificate);
};

export const createTrainingCompletion = async (data: {
  assignmentId: number;
  userId: number;
  moduleId: number;
  score: number;
  percentageScore: number;
  passed: boolean;
  timeSpentMinutes: number;
}): Promise<TrainingCompletion> => {
  await delay();
  
  // Update the assignment status
  const assignmentIndex = mockTrainingAssignments.findIndex(a => a.id === data.assignmentId);
  if (assignmentIndex !== -1) {
    mockTrainingAssignments[assignmentIndex].status = data.passed ? 5 : 6; // Completed : Failed
    mockTrainingAssignments[assignmentIndex].completedDate = new Date().toISOString();
    mockTrainingAssignments[assignmentIndex].score = data.percentageScore;
  }
  
  // Create completion record
  const newCompletion: TrainingCompletion = {
    id: Math.max(0, ...mockTrainingCompletions.map(c => c.id)) + 1,
    assignmentId: data.assignmentId,
    userId: data.userId,
    moduleId: data.moduleId,
    moduleName: mockTrainingModules.find(m => m.id === data.moduleId)?.name || 'Unknown Module',
    completedDate: new Date().toISOString(),
    score: data.score,
    passed: data.passed,
    certificateId: null,
    validUntil: null,
  };
  mockTrainingCompletions.push(newCompletion);
  
  // Generate certificate if passed
  if (data.passed) {
    const newCertificate: TrainingCertificate = {
      id: Math.max(0, ...mockTrainingCertificates.map(c => c.id)) + 1,
      userId: data.userId,
      userName: 'Current User',
      moduleId: data.moduleId,
      moduleName: mockTrainingModules.find(m => m.id === data.moduleId)?.name || 'Unknown Module',
      completionId: newCompletion.id,
      issueDate: new Date().toISOString(),
      validUntil: null,
      certificateNumber: `CERT-${Date.now()}-${data.userId}-${data.moduleId}`,
      isRevoked: false,
      revokedDate: null,
      revokedReason: null,
    };
    mockTrainingCertificates.push(newCertificate);
    newCompletion.certificateId = newCertificate.id;
  }
  
  return clone(newCompletion);
};

// ========================================
// ACTION ITEMS
// ========================================
export const getAllActionItems = async (storeNumber?: string, status?: string, assignedToUserId?: number): Promise<ActionItem[]> => {
  await delay();
  let items = mockActionItems;
  
  if (storeNumber) {
    items = items.filter(i => i.storeNumber === storeNumber);
  }
  
  if (status) {
    items = items.filter(i => i.status === status);
  }
  
  if (assignedToUserId) {
    items = items.filter(i => i.assignedToUserId === assignedToUserId);
  }
  
  return clone(items);
};

export const getActionItemById = async (id: number, _storeNumber?: string): Promise<ActionItem> => {
  await delay();
  const item = mockActionItems.find(i => i.id === id);
  if (!item) throw new Error(`Action item with id ${id} not found`);
  return clone(item);
};

export const createActionItem = async (data: Omit<ActionItem, 'id' | 'createdDate' | 'resolvedDate' | 'resolvedBy'>): Promise<ActionItem> => {
  await delay();
  const newItem: ActionItem = {
    ...data,
    id: Math.max(...mockActionItems.map(i => i.id)) + 1,
    createdDate: new Date().toISOString(),
    resolvedDate: null,
    resolvedBy: null,
  };
  mockActionItems.push(newItem);
  return clone(newItem);
};

export const updateActionItem = async (id: number, data: Partial<ActionItem>, _storeNumber?: string): Promise<ActionItem> => {
  await delay();
  const index = mockActionItems.findIndex(i => i.id === id);
  if (index === -1) throw new Error(`Action item with id ${id} not found`);
  
  mockActionItems[index] = {
    ...mockActionItems[index],
    ...data,
  };
  return clone(mockActionItems[index]);
};

export const resolveActionItem = async (id: number, resolutionNotes: string, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockActionItems.findIndex(i => i.id === id);
  if (index === -1) throw new Error(`Action item with id ${id} not found`);
  
  mockActionItems[index] = {
    ...mockActionItems[index],
    status: 'Resolved',
    resolvedDate: new Date().toISOString(),
    resolvedBy: 'mock.user@orderly.com',
    resolutionNotes,
  };
};

export const closeActionItem = async (id: number, _storeNumber: string | undefined, closeNotes: string): Promise<void> => {
  await delay();
  const index = mockActionItems.findIndex(i => i.id === id);
  if (index === -1) throw new Error(`Action item with id ${id} not found`);
  
  mockActionItems[index] = {
    ...mockActionItems[index],
    status: 'Closed',
    closedDate: new Date().toISOString(),
    closedBy: 'Current User',
    closeNotes,
  };
};

export const addActionItemFeedback = async (id: number, _storeNumber: string | undefined, feedback: string): Promise<ActionItem> => {
  await delay();
  const index = mockActionItems.findIndex(i => i.id === id);
  if (index === -1) throw new Error(`Action item with id ${id} not found`);
  
  const entry = {
    id: Date.now(),
    actionItemId: id,
    createdByUserId: 1,
    createdByUserName: 'Current User',
    comment: feedback,
    createdDate: new Date().toISOString(),
  };
  
  if (!mockActionItems[index].comments) {
    mockActionItems[index].comments = [];
  }
  mockActionItems[index].comments!.push(entry);
  
  return clone(mockActionItems[index]);
};

export const deleteActionItem = async (id: number, _storeNumber?: string): Promise<void> => {
  await delay();
  const index = mockActionItems.findIndex(i => i.id === id);
  if (index === -1) throw new Error(`Action item with id ${id} not found`);
  mockActionItems.splice(index, 1);
};

// ========================================
// JOB ROLES
// ========================================
export const getAllJobRoles = async (includeInactive: boolean = false): Promise<JobRole[]> => {
  await delay();
  const roles = includeInactive ? mockJobRoles : mockJobRoles.filter(r => r.isActive);
  return clone(roles);
};

export const getJobRoleById = async (id: number): Promise<JobRole> => {
  await delay();
  const role = mockJobRoles.find(r => r.id === id);
  if (!role) throw new Error(`Job role with id ${id} not found`);
  return clone(role);
};

export const createJobRole = async (data: Omit<JobRole, 'id'>): Promise<JobRole> => {
  await delay();
  const newRole: JobRole = {
    ...data,
    id: Math.max(...mockJobRoles.map(r => r.id)) + 1,
  };
  mockJobRoles.push(newRole);
  return clone(newRole);
};

export const updateJobRole = async (id: number, data: Partial<JobRole>): Promise<JobRole> => {
  await delay();
  const index = mockJobRoles.findIndex(r => r.id === id);
  if (index === -1) throw new Error(`Job role with id ${id} not found`);
  
  mockJobRoles[index] = {
    ...mockJobRoles[index],
    ...data,
  };
  return clone(mockJobRoles[index]);
};

export const deleteJobRole = async (id: number): Promise<void> => {
  await delay();
  const index = mockJobRoles.findIndex(r => r.id === id);
  if (index === -1) throw new Error(`Job role with id ${id} not found`);
  mockJobRoles.splice(index, 1);
};

// ========================================
// NOTIFICATIONS
// ========================================
export const getNotificationsByUserId = async (userId: number, includeRead: boolean = true): Promise<ComplianceNotification[]> => {
  await delay();
  let notifications = mockNotifications.filter(n => n.userId === userId);
  
  if (!includeRead) {
    notifications = notifications.filter(n => !n.isRead);
  }
  
  return clone(notifications);
};

export const markNotificationAsRead = async (id: number): Promise<ComplianceNotification> => {
  await delay();
  const index = mockNotifications.findIndex(n => n.id === id);
  if (index === -1) throw new Error(`Notification with id ${id} not found`);
  
  mockNotifications[index].isRead = true;
  mockNotifications[index].readDate = new Date().toISOString();
  
  return clone(mockNotifications[index]);
};

export const markAllNotificationsAsRead = async (userId: number): Promise<void> => {
  await delay();
  const now = new Date().toISOString();
  mockNotifications.forEach(n => {
    if (n.userId === userId && !n.isRead) {
      n.isRead = true;
      n.readDate = now;
    }
  });
};

// ========================================
// AUDIT REPORTS (Mock)
// ========================================

const buildMockAuditSummary = (_filter: AuditReportFilter): AuditSummaryReport => ({
  totalAudits: mockAudits.length,
  completedAudits: mockAudits.filter(a => a.status === 3).length,
  averageScore: 82.5,
  complianceRate: 87.3,
  byStatus: { Draft: 2, Submitted: 5 },
  byStore: mockStores.slice(0, 3).map(s => ({ storeNumber: s.storeNumber, storeName: s.storeName, auditCount: 3, averageScore: 80 })),
  byType: [{ auditTypeName: 'Food Safety Audit', auditCount: 4, averageScore: 85 }],
  period: { startDate: _filter.startDate, endDate: _filter.endDate },
});

const buildMockActionItemsSummary = (_filter: AuditReportFilter): ActionItemsSummaryReport => ({
  totalItems: mockActionItems.length,
  openItems: mockActionItems.filter(a => a.status === 'Open').length,
  resolvedItems: mockActionItems.filter(a => a.status === 'Resolved').length,
  overdueItems: 2,
  byPriority: { High: 3, Medium: 5, Low: 2 },
  byStore: mockStores.slice(0, 3).map(s => ({ storeNumber: s.storeNumber, storeName: s.storeName, itemCount: 3, overdueCount: 1 })),
  period: { startDate: _filter.startDate, endDate: _filter.endDate },
});

export const generateAuditSummaryReport = async (filter: AuditReportFilter): Promise<AuditSummaryReport> => {
  await delay();
  return buildMockAuditSummary(filter);
};

export const previewAuditSummaryReport = async (filter: AuditReportFilter): Promise<AuditSummaryReport> => {
  await delay();
  return buildMockAuditSummary(filter);
};

export const exportAuditSummaryReport = async (_filter: AuditReportFilter): Promise<Blob> => {
  await delay();
  return new Blob(['Mock Audit Summary Excel'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

export const generateAuditDetailReport = async (filter: AuditReportFilter): Promise<AuditDetailReport> => {
  await delay();
  return {
    audits: mockAudits.slice(0, 5).map(a => ({
      id: a.id,
      auditTypeName: a.auditTypeName ?? '',
      storeNumber: a.storeNumber ?? '',
      storeName: a.storeName ?? '',
      status: a.status,
      scheduledDate: a.scheduledDate ?? '',
      completedDate: a.completedDate ?? undefined,
      score: a.score ?? undefined,
      conductedByUserName: a.conductedByUserName ?? undefined,
      approvedByUserName: a.secondSignatureUserName ?? undefined,
    })),
    period: { startDate: filter.startDate, endDate: filter.endDate },
  };
};

export const previewAuditDetailReport = async (filter: AuditReportFilter): Promise<AuditDetailReport> => {
  await delay();
  return generateAuditDetailReport(filter);
};

export const exportAuditDetailReport = async (_filter: AuditReportFilter): Promise<Blob> => {
  await delay();
  return new Blob(['Mock Audit Detail Excel'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

export const generateActionItemsSummaryReport = async (filter: AuditReportFilter): Promise<ActionItemsSummaryReport> => {
  await delay();
  return buildMockActionItemsSummary(filter);
};

export const previewActionItemsSummaryReport = async (filter: AuditReportFilter): Promise<ActionItemsSummaryReport> => {
  await delay();
  return buildMockActionItemsSummary(filter);
};

export const exportActionItemsSummaryReport = async (_filter: AuditReportFilter): Promise<Blob> => {
  await delay();
  return new Blob(['Mock Action Items Summary Excel'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

export const generateComplianceOverviewReport = async (filter: AuditReportFilter): Promise<ComplianceOverviewReport> => {
  await delay();
  return {
    auditSummary: buildMockAuditSummary(filter),
    actionItemsSummary: buildMockActionItemsSummary(filter),
    period: { startDate: filter.startDate, endDate: filter.endDate },
  };
};

export const previewComplianceOverviewReport = async (filter: AuditReportFilter): Promise<ComplianceOverviewReport> => {
  await delay();
  return generateComplianceOverviewReport(filter);
};

export const exportComplianceOverviewReport = async (_filter: AuditReportFilter): Promise<Blob> => {
  await delay();
  return new Blob(['Mock Compliance Overview Excel'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};
